import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Checkbox from '@mulesoft/anypoint-components/lib/Checkbox';
import InfoIcon from '@mulesoft/anypoint-icons/lib/svg/info.svg';
import MoreIcon from '@mulesoft/anypoint-icons/lib/svg/more-small.svg';
import { getPath } from '~/utils/routes';
import { ASSET_TYPE_LABELS } from '~/components/AgentScanners/types/commonTypes';
import styles from './TabPanelReview.css';

const DEFAULT_ERROR_MESSAGE =
  'Something went wrong in the scan run. Will retry in next scan.';

const getCard = (item) => item.agentCard?.card || item.agentCard || {};

const TabPanelReview = ({
  title,
  subtitle,
  typeStyle,
  agents,
  region,
  typeMsg,
  typeMsgStyle,
  displayWarning = false,
  warningMessage,
  warningVariant = 'error',
  showCheckboxes = false,
  selectedAgentIds,
  onToggleAgent,
  onSelectAll,
  onDeselectAll,
  onRemoveFromSkipped,
  assetType = '',
  platformLabel = '',
  disableLink = false
}) => {
  const [openMenuId, setOpenMenuId] = React.useState(null);
  const menuRef = React.useRef(null);
  const agentIds = agents.map((a) => a.id);
  const allSelected =
    agents.length > 0 && agents.every((a) => selectedAgentIds?.has(a.id));
  const someSelected =
    !allSelected && agents.some((a) => selectedAgentIds?.has(a.id));

  React.useEffect(() => {
    if (openMenuId === null) return undefined;

    const handleOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpenMenuId(null);
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openMenuId]);

  const handleToggleMenu = (id) => {
    setOpenMenuId((prev) => (prev === id ? null : id));
  };

  const handleRemoveFromSkipped = (agent) => {
    setOpenMenuId(null);
    onRemoveFromSkipped(agent);
  };

  const handleToggleAll = () => {
    if (allSelected) {
      onDeselectAll(agentIds);
    } else {
      onSelectAll(agentIds);
    }
  };

  return (
    <div data-test-id="scan-tab-panel-section">
      <div className={styles.reviewSectionTitle}>
        <p className={styles.reviewInfoLabel}>{title}</p>
        <p>{subtitle}</p>
      </div>

      {showCheckboxes && agents.length > 0 && (
        <div className={styles.selectAllRow}>
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={handleToggleAll}
            data-test-id="select-all-checkbox"
          />
          <button
            type="button"
            className={styles.selectAllLabel}
            onClick={handleToggleAll}
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
        </div>
      )}

      <div display-if={agents.length > 0}>
        {agents.map((item) => {
          const path = getPath('assetHome', {
            groupId: item.organizationId,
            assetId: item.assetId
          });
          const isChecked = selectedAgentIds?.has(item.id) ?? false;
          // Prefer the per-asset type the backend resolved (e.g. GRPC_API, GRAPHQL) over the
          // scanner-level default. API scanners like Kong are configured as 'rest_api' but can
          // discover gRPC/GraphQL services; the backend surfaces the concrete type per asset and
          // leaves it empty when the scanner-level type already applies. Normalize to the lowercase
          // key used by ASSET_TYPE_LABELS.
          const serviceType = (item.assetType || '').toLowerCase() || assetType;

          return (
            <div
              key={item.id}
              className={`${styles.reviewCardRow} ${typeStyle}`}
            >
              {showCheckboxes && (
                <div className={styles.reviewCardCheckbox}>
                  <Checkbox
                    checked={isChecked}
                    onChange={() => onToggleAgent(item.id)}
                    data-test-id={`agent-checkbox-${item.id}`}
                  />
                </div>
              )}
              <div
                className={`${styles.reviewInfoGrid} ${styles.reviewCardBody}`}
              >
                <div className={styles.reviewInfoItem}>
                  <span>
                    <span className={styles.reviewInfoLabel}>
                      {disableLink ? (
                        <span data-test-id="go-to-asset">{item.name}</span>
                      ) : (
                        <Link data-test-id="go-to-asset" to={path}>
                          {item.name}
                        </Link>
                      )}
                    </span>
                    <span className={styles.reviewInfoItemSkill}>
                      {item.version}
                    </span>
                    <span className={`${typeMsgStyle}`}>{typeMsg}</span>
                    <span className={styles.reviewInfoType}>
                      {platformLabel}
                    </span>
                  </span>
                  <span className={styles.reviewInfoTarget}>
                    {item.description}
                  </span>
                  <span
                    data-test-id="impact-analysis"
                    className={
                      warningVariant === 'partial'
                        ? styles.reviewInfoWarningPartial
                        : styles.reviewInfoWarningError
                    }
                    display-if={
                      displayWarning && item.operationPerformed !== 'PENDING'
                    }
                  >
                    <InfoIcon />
                    <strong>Impact Analysis:</strong>{' '}
                    {warningMessage ||
                      item.summary?.summary ||
                      DEFAULT_ERROR_MESSAGE}
                  </span>
                </div>
                <div className={styles.reviewInfoItem}>
                  <div className={styles.reviewInfoItemDetails}>
                    <div className={styles.reviewInfoGrid}>
                      <div className={styles.reviewInfoItem}>Platform</div>
                      <div className={styles.reviewInfoItem}>
                        {platformLabel}
                      </div>
                    </div>
                    <div className={styles.reviewInfoGrid}>
                      <div className={styles.reviewInfoItem}>Service Type</div>
                      <div className={styles.reviewInfoItem}>
                        {ASSET_TYPE_LABELS[serviceType] || serviceType}
                      </div>
                    </div>
                    {assetType && assetType !== 'agent' ? (
                      <div className={styles.reviewInfoGrid}>
                        <div className={styles.reviewInfoItem}>Instances</div>
                        <div className={styles.reviewInfoItem}>
                          {item.assetPayload?.instances?.length ??
                            (item.assetPayload?.instance ? 1 : 0)}
                        </div>
                      </div>
                    ) : (
                      <React.Fragment>
                        <div className={styles.reviewInfoGrid}>
                          <div className={styles.reviewInfoItem}>
                            Foundation model
                          </div>
                          <div className={styles.reviewInfoItem}>{}</div>
                        </div>
                        <div className={styles.reviewInfoGrid}>
                          <div className={styles.reviewInfoItem}>Agent ID</div>
                          <div className={styles.reviewInfoItem}>
                            {item.assetId}
                          </div>
                        </div>
                        <div className={styles.reviewInfoGrid}>
                          <div className={styles.reviewInfoItem}>Region</div>
                          <div className={styles.reviewInfoItem}>{region}</div>
                        </div>
                        <div className={styles.reviewInfoGrid}>
                          <div className={styles.reviewInfoItem}>Skills</div>
                          <div display-if={getCard(item).skills?.length > 0}>
                            {(getCard(item).skills || []).map((skillItem) => (
                              <span
                                key={skillItem.name}
                                className={styles.reviewInfoItemSkill}
                              >
                                {skillItem.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      </React.Fragment>
                    )}
                  </div>
                </div>
              </div>
              {onRemoveFromSkipped && (
                <div
                  className={styles.cardMenuWrapper}
                  ref={openMenuId === item.id ? menuRef : undefined}
                >
                  <button
                    type="button"
                    className={styles.cardMenuTrigger}
                    onClick={() => handleToggleMenu(item.id)}
                    data-test-id={`skipped-menu-${item.id}`}
                    aria-label="More options"
                    aria-haspopup="menu"
                    aria-expanded={openMenuId === item.id}
                  >
                    <MoreIcon />
                  </button>
                  {openMenuId === item.id && (
                    <div className={styles.cardMenuDropdown} role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        className={styles.cardMenuOption}
                        onClick={() => handleRemoveFromSkipped(item)}
                        data-test-id={`remove-from-skipped-${item.id}`}
                      >
                        Remove from Skipped
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

TabPanelReview.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  typeStyle: PropTypes.string,
  agents: PropTypes.array,
  region: PropTypes.string,
  typeMsg: PropTypes.string,
  typeMsgStyle: PropTypes.string,
  displayWarning: PropTypes.bool,
  // Customer-facing Impact Analysis text; when set it replaces the backend summary.
  warningMessage: PropTypes.string,
  warningVariant: PropTypes.oneOf(['error', 'partial']),
  showCheckboxes: PropTypes.bool,
  selectedAgentIds: PropTypes.instanceOf(Set),
  onToggleAgent: PropTypes.func,
  onSelectAll: PropTypes.func,
  onDeselectAll: PropTypes.func,
  onRemoveFromSkipped: PropTypes.func,
  assetType: PropTypes.string,
  platformLabel: PropTypes.string,
  disableLink: PropTypes.bool
};

export default TabPanelReview;
